import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSearch from '../../hooks/useSearch';
import {
    getAssetTypes,
    getUomsForValidation,
    SensorUomResponse,
    downloadConfigFiles,
} from '../../Services/serviceURL';
import {
    AssetType,
    AssetDetails,
    PlantData,
    UIParameter,
    mapToUIParameters,
    validateSimNumber,
    plantHasSavedAssets,
    detectAssetFormChanges,
} from './createPlantDetailsModel';
import {
    buildAssetDetailsFromSave,
    buildOriginalAssetSnapshot,
    extractPlantAssetSaveErrorMessage,
    getPostNewAssetSaveReset,
    persistPlantAssetToLocalStorage,
    submitPlantAssetSave,
    validatePlantAssetBeforeSave,
    type PlantAssetSaveFormState,
    type PlantAssetSaveValidationFailure,
} from './createPlantDetailsSave';
import {
    buildAssetParametersForSelection,
    buildOriginalAssetDataFromClick,
} from './createPlantDetailsAssetSelection';
import { loadCreatePlantDetailsData } from './createPlantDetailsPlantFetch';
import type { CreatePlantDetailsViewProps } from './CreatePlantDetailsView';

export function useCreatePlantDetails(): CreatePlantDetailsViewProps {
    const [selectedAsset, setSelectedAsset] = useState<string>('');
    const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [assetName, setAssetName] = useState<string>('');
    const [assetStatus, setAssetStatus] = useState<string>('Active');
    const [parameters, setParameters] = useState<UIParameter[]>([]);
    const [plantData, setPlantData] = useState<PlantData | null>(null);
    const [assetHierarchy, setAssetHierarchy] = useState<AssetDetails[]>([]);
    const {
        searchText: assetSearchText,
        filteredData: filteredAssets,
        handleSearchChange: handleAssetSearchChange,
        resetSearch: resetAssetSearch,
    } = useSearch(assetHierarchy);
    const [selectedAssetFromHierarchy, setSelectedAssetFromHierarchy] = useState<AssetDetails | null>(null);
    const [currentPlantData, setCurrentPlantData] = useState<any>(null); // Add this

    // New state variables for additional fields
    const [gatewayDeviceType, setGatewayDeviceType] = useState<string>('SLE');
    const [gatewayDeviceId, setGatewayDeviceId] = useState<string>('');
    const [simNumber, setSimNumber] = useState<string>('');
    const [simNumberError, setSimNumberError] = useState<string>('');
    const [simNumberTouched, setSimNumberTouched] = useState<boolean>(false);
    const [controllerType, setControllerType] = useState<string>('');
    const [controllerDeviceId, setControllerDeviceId] = useState<string>('');
    const [gatewaySerialError, setGatewaySerialError] = useState<string>('');
    const [gatewaySerialTouched, setGatewaySerialTouched] = useState<boolean>(false);
    const [availableControllerTypes, setAvailableControllerTypes] = useState<string[]>([]);
    const navigate = useNavigate();
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [id, setId] = useState<string>('');
    const [validUoms, setValidUoms] = useState<SensorUomResponse[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const isRefreshingAfterSave = useRef<boolean>(false);
    const lastFetchedId = useRef<string>('');
    const getGatewaySerialValidationMessage = useCallback((serial: string) => {
        const trimmedSerial = (serial || '').trim();
        if (!trimmedSerial) {
            return '';
        }

        const duplicateAsset = assetHierarchy.find(
            (asset) =>
                asset.gatewayDeviceId?.trim().toLowerCase() === trimmedSerial.toLowerCase() &&
                asset.id !== selectedAssetId
        );

        return duplicateAsset ? 'Gateway serial number already in use' : '';
    }, [assetHierarchy, selectedAssetId]);

    // State to track original asset data for change detection
    const [originalAssetData, setOriginalAssetData] = useState<{
        selectedAsset: string;
        assetName: string;
        assetStatus: string;
        gatewayDeviceType: string;
        gatewayDeviceId: string;
        simNumber: string;
        controllerType: string;
        controllerDeviceId: string;
        parameters: UIParameter[];
    } | null>(null);

 useEffect(() => {
        // Check localStorage for currentPlantData
        const storedPlantData = localStorage.getItem('currentPlantData');
        if (storedPlantData) {
            try {
                const parsedData = JSON.parse(storedPlantData);
                setCurrentPlantData(parsedData);
            } catch (error) {
                console.error('Error parsing currentPlantData:', error);
            }
        }
    }, []); // Run once on component mount
  // Also reset isInitialLoad when plant changes

    // Track previous plant ID to detect changes
    const [prevPlantId, setPrevPlantId] = useState<string>('');
    useEffect(() => {
        const navState = globalThis.history.state?.usr; // router passes state here
        const passedPlantId = navState?.plantId;

        // Try to get ObjectId for internal tracking (this is used for navigation state)
        const fallbackPlantId = localStorage.getItem('currentPlantId');
        const finalPlantId = passedPlantId || fallbackPlantId;

        if (!finalPlantId) {
            // Check if we at least have a GUID
            const plantGuid = localStorage.getItem('currentplantguid');
            if (!plantGuid) {
                setError('No plant ID found');
                navigate('/plants');
                return;
            }
        }

        // Check if plant ID has changed
        if (prevPlantId && prevPlantId !== finalPlantId) {
            // Reset all form and hierarchy state when switching plants
            resetAllFields();
            setAssetHierarchy([]);
            setPlantData(null);
            setSelectedAssetFromHierarchy(null);
            setOriginalAssetData(null);
            setError(null);
        }

        if (finalPlantId) {
            setId(finalPlantId); // update your internal ID state here
            localStorage.setItem('currentPlantId', finalPlantId); // keep both in sync
            // Don't set prevPlantId here - let the fetch useEffect handle it
        }
    }, [navigate, prevPlantId]);

      useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousHtmlOverflow;
        };
      }, []);

    useEffect(() => {
        const fetchAssetTypes = async () => {
            setLoading(true);
            try {
                // Fetch both asset types and valid UOMs in parallel
                const [assetTypesResponse, uomsResponse] = await Promise.all([
                    getAssetTypes(),
                    getUomsForValidation()
                ]);
                
                // Set valid UOMs for custom probe validation
                setValidUoms(uomsResponse || []);

                if (assetTypesResponse.Success && Array.isArray(assetTypesResponse.Data)) {

                    setAssetTypes(assetTypesResponse.Data);

                    if (assetTypesResponse.Data.length > 0) {
                        const firstAsset = assetTypesResponse.Data[0];
                        setSelectedAsset(firstAsset.typeName);
                        // Set initial controller type - handle cases where it might be undefined
                        const initialControllerType = firstAsset.controllerType || '';
                        setControllerType(initialControllerType);
                        // Only show the controllerType for the first asset type if it exists
                        setAvailableControllerTypes(initialControllerType ? [initialControllerType] : []);

                        // Transform API parameters into UIParameter structure with proper unit options
                        const initialParameters = mapToUIParameters(firstAsset.parameters, uomsResponse || [], true);
                        setParameters(initialParameters);
                    }
                } else {
                    setError('No asset types found');
                }
            } catch (err) {
                console.error('Failed to load asset types:', err);
                setError('Failed to load asset types');
            } finally {
                setLoading(false);
            }
        };

        fetchAssetTypes();
    }, []);

    // Update unit options for existing parameters when asset types are loaded
    useEffect(() => {
        if (assetTypes.length > 0 && parameters.length > 0 && validUoms.length > 0) {
            const updatedParameters = parameters.map(param => {
                // Find the asset type for the current selected asset
                const currentAssetType = assetTypes.find(type => type.typeName === selectedAsset);
                if (currentAssetType) {
                    // Find the matching parameter in the asset type to get unit options
                    const typeParam = currentAssetType.parameters.find(tp => tp.sensorName === param.sensor);
                    if (typeParam) {
                        let unitOptions = Array.isArray(typeParam.units) ? typeParam.units : [];
                        
                        // For custom probes (isUserConfigurable: true), use UOMs from validation API
                        if (typeParam.isUserConfigurable === true) {
                            const sensorUoms = validUoms.find(uom => uom.sensorName === param.sensor);
                            if (sensorUoms) {
                                unitOptions = sensorUoms.uoms;

                            }
                        }
                        
                        return {
                            ...param,
                            unitOptions: unitOptions
                        };
                    }
                }
                return param;
            });
            
            // Only update if there are actual changes to unit options
            const hasChanges = updatedParameters.some((param, index) => 
                JSON.stringify(param.unitOptions) !== JSON.stringify(parameters[index].unitOptions)
            );
            
            if (hasChanges) {
                setParameters(updatedParameters);
            }
        }
    }, [assetTypes, selectedAsset, validUoms]);


    // Handle asset type changes only when not loading from hierarchy
    useEffect(() => {
        // Skip if asset is being loaded from hierarchy or if no asset types are available
        if (selectedAssetFromHierarchy || assetTypes.length === 0) {
            return;
        }

        const selectedAssetType = assetTypes.find(type => type.typeName === selectedAsset);
        if (selectedAssetType) {

            
            // Only update parameters, don't clear other fields unless it's a new selection
            const newParameters = mapToUIParameters(selectedAssetType.parameters, validUoms, !selectedAssetFromHierarchy);

            setParameters(newParameters);
            
            // Update controller type based on asset type
            updateControllerType(selectedAsset);
        }
    }, [selectedAsset, assetTypes, selectedAssetFromHierarchy]);

    // Initialize default values when component first loads
    useEffect(() => {
        if (assetTypes.length > 0 && !selectedAsset && !selectedAssetFromHierarchy) {
            const firstAssetType = assetTypes[0];
            setSelectedAsset(firstAssetType.typeName);
            updateControllerType(firstAssetType.typeName);
        }
    }, [assetTypes, selectedAsset, selectedAssetFromHierarchy]);

    useEffect(() => {
        if (gatewaySerialTouched) {
            setGatewaySerialError(getGatewaySerialValidationMessage(gatewayDeviceId));
        }
    }, [assetHierarchy, selectedAssetId, gatewayDeviceId, gatewaySerialTouched, getGatewaySerialValidationMessage]);

    const handleGatewaySerialChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setGatewayDeviceId(value);

        if (gatewaySerialTouched) {
            setGatewaySerialError(getGatewaySerialValidationMessage(value));
        } else if (gatewaySerialError) {
            setGatewaySerialError('');
        }
    };

    const handleGatewaySerialBlur = () => {
        setGatewaySerialTouched(true);
        setGatewaySerialError(getGatewaySerialValidationMessage(gatewayDeviceId));
    };

    const resetGatewaySerialValidation = () => {
        setGatewaySerialTouched(false);
        setGatewaySerialError('');
    };
    const handleSimNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = event.target.value.replaceAll(/\s/g, '').replaceAll(/\D/g, '');
        setSimNumber(sanitized);

        if (simNumberTouched) {
            setSimNumberError(validateSimNumber(sanitized));
        }
    };

    const handleSimNumberBlur = () => {
        setSimNumberTouched(true);
        setSimNumberError(validateSimNumber(simNumber));
    };

   const resetSimNumberValidation = () => {
    setSimNumberTouched(false);
    setSimNumberError('');
   };
    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = e.target.value;
        
        // Clear asset hierarchy selection when manually changing type
        if (selectedAssetFromHierarchy) {
            setSelectedAssetFromHierarchy(null);
            setSelectedAssetId(null);
            setOriginalAssetData(null);
        }
        
        setSelectedAsset(selected);
        
        // Clear form fields for new asset type selection
        setAssetName('');
        setAssetStatus('Active');
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        resetGatewaySerialValidation();
        setSimNumber('');
        setControllerDeviceId('');
        
        // Update controller type and parameters
        updateControllerType(selected);
        
        const matchedAsset = assetTypes.find(item => item.typeName === selected);
        if (matchedAsset) {
            const updatedParameters = mapToUIParameters(matchedAsset.parameters, validUoms, true);
            setParameters(updatedParameters);
        }
    };
    const resetAllFields = () => {
        // Clear all form state
        setSelectedAssetFromHierarchy(null);
        setSelectedAssetId(null);
        setOriginalAssetData(null);
        setAssetName('');
        setAssetStatus('Active');
        resetAssetSearch();
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        setSimNumber('');
        setControllerDeviceId('');
        setError(null);
         //reset validation states
         resetGatewaySerialValidation();
         resetSimNumberValidation();
        // Reset to first asset type if available
        if (assetTypes.length > 0) {
            const firstAssetType = assetTypes[0];
            setSelectedAsset(firstAssetType.typeName);
            const controllerTypeValue = firstAssetType.controllerType || '';
            setControllerType(controllerTypeValue);
            setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);
            
            const defaultParams = mapToUIParameters(firstAssetType.parameters, validUoms, true);
            setParameters(defaultParams);
        } else {
            setSelectedAsset('');
            setControllerType('');
            setAvailableControllerTypes([]);
            setParameters([]);
        }
    };

    const buildSaveFormState = (): PlantAssetSaveFormState => ({
        selectedAssetId,
        selectedAsset,
        assetName,
        assetStatus,
        gatewayDeviceType,
        gatewayDeviceId,
        simNumber,
        controllerType,
        controllerDeviceId,
        parameters,
    });

    const applyPlantAssetSaveValidationFailure = (failure: PlantAssetSaveValidationFailure) => {
        if (failure.simNumberError) {
            setSimNumberTouched(true);
            setSimNumberError(failure.simNumberError);
        }
        if (failure.gatewaySerialError) {
            setGatewaySerialTouched(true);
            setGatewaySerialError(failure.gatewaySerialError);
        }
        toast.error(failure.message);
    };

    const handleSave = async () => {
        if (loading) {
            return;
        }

        try {
            setLoading(true);
            setIsSaving(true);
            isRefreshingAfterSave.current = true;
            setError(null);

            if (selectedAssetFromHierarchy && !hasAssetChanges()) {
                toast.info('No changes detected. Save operation cancelled.');
                return;
            }

            const form = buildSaveFormState();
            const validation = validatePlantAssetBeforeSave({
                form,
                assetHierarchy,
                assetTypes,
                validUoms,
                getGatewaySerialValidationMessage,
            });

            if (!validation.ok) {
                applyPlantAssetSaveValidationFailure(validation.failure);
                return;
            }

            const response = await submitPlantAssetSave(form, validation.data);
            if (!response.Success) {
                toast.error(response.Message || 'Failed to save asset. Please try again.');
                return;
            }

            const assetData = buildAssetDetailsFromSave(
                form,
                validation.data.mappedParameters,
                response.Data
            );

            if (selectedAssetId) {
                setAssetHierarchy((prevHierarchy) =>
                    prevHierarchy.map((asset) =>
                        asset.id === selectedAssetId ? assetData : asset
                    )
                );
                setPlantData((prevPlantData) => {
                    if (!prevPlantData) {
                        return prevPlantData;
                    }
                    return {
                        ...prevPlantData,
                        assets: ((prevPlantData.assets as AssetDetails[]) || []).map((asset) =>
                            asset.id === selectedAssetId ? assetData : asset
                        ),
                    };
                });
                setOriginalAssetData(buildOriginalAssetSnapshot(form));
            } else {
                setAssetHierarchy((prevHierarchy) => [...prevHierarchy, assetData]);
                setPlantData((prevPlantData) => {
                    if (!prevPlantData) {
                        return {
                            customerId: '',
                            customerName: '',
                            siteId: '',
                            siteName: '',
                            plantName: '',
                            plantStatus: 'Active',
                            assets: [assetData],
                        };
                    }
                    return {
                        ...prevPlantData,
                        assets: [...(prevPlantData.assets || []), assetData],
                    };
                });
            }

            persistPlantAssetToLocalStorage(assetData, selectedAssetId, plantData);

            if (!selectedAssetId) {
                resetAllFields();
                const postReset = getPostNewAssetSaveReset(assetTypes, selectedAsset);
                if (postReset.resetOnlyName) {
                    setAssetName('');
                } else if (postReset.firstAssetType) {
                    setSelectedAsset(postReset.firstAssetType.typeName);
                    setAssetStatus('Active');
                    setAssetName('');
                    setParameters(
                        mapToUIParameters(postReset.firstAssetType.parameters, validUoms, true)
                    );
                }
            }

            toast.success('Asset saved successfully!');
            globalThis.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error('Failed to save plant asset:', err);
            toast.error(extractPlantAssetSaveErrorMessage(err));
        } finally {
            setLoading(false);
            setIsSaving(false);
            setTimeout(() => {
                isRefreshingAfterSave.current = false;
            }, 100);
        }
    };
    const handleCancel = () => {
        // Reset all form fields to their initial values
        setSelectedAsset(assetTypes.length > 0 ? assetTypes[0].typeName : '');
        setAssetName('');
        setAssetStatus('Active');
        resetAssetSearch();
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        setSimNumber('');
        setControllerType('');
        setControllerDeviceId('');
        resetGatewaySerialValidation();

        // Reset parameters based on the first asset type
        if (assetTypes.length > 0) {
            const initialParameters = mapToUIParameters(assetTypes[0].parameters, validUoms, true);

            setParameters(initialParameters);
        } else {
            setParameters([]);
        }

        navigate('/plants');
    };

    // Tab navigation handler
    const handleTabNavigation = (tabNumber: number) => {
        const plantGuid = localStorage.getItem('currentplantguid');
        
        switch (tabNumber) {
            case 1:
                // Navigate back to Create Plant page
                if (plantGuid) {
                    navigate(`/create-plant/${plantGuid}`);
                } else {
                    navigate('/create-plant');
                }
                break;
            case 2:
                // Current page - do nothing
                break;
            case 3:
                // Navigate to Notifications
                navigate('/create-plant-details/notification');
                break;
            default:
                break;
        }
    };
    
    const handleAddAsset = () => {
        // Use the resetAllFields function to ensure consistent reset behavior
        resetAllFields();
    };

    // Function to handle downloading config files
    const handleDownloadConfigFiles = async () => {
        if (!selectedAssetFromHierarchy) {
            toast.warn('Please select an asset to download config files.');
            return;
        }

        try {
            setLoading(true);
            
            // Use the API to download config files
            const result = await downloadConfigFiles(selectedAssetFromHierarchy.id);
            
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            console.error('Error downloading config files:', error);
            toast.error('Failed to download config files. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        // Use proper plant GUID for navigation (URL parameter)
        const plantGuid = localStorage.getItem('currentplantguid');
        if (plantGuid) {
            navigate(`/create-plant/${plantGuid}`); // Go back to edit mode for the same plant
        } else {
            navigate('/plants'); // Fallback
        }
    };

    const handleAssetClick = (asset: AssetDetails) => {
        setSelectedAssetFromHierarchy(asset);
        setSelectedAsset(asset.type);
        setAssetName(asset.name);
        setAssetStatus(asset.status);
        setSelectedAssetId(asset.id);
        setGatewayDeviceType(asset.gatewayDeviceType ?? '');
        setGatewayDeviceId(asset.gatewayDeviceId ?? '');
        resetGatewaySerialValidation();
        setSimNumber(asset.simNumber ?? '');
        setControllerDeviceId(asset.controllerDeviceId ?? '');

        const matchingAssetType = assetTypes.find((type) => type.typeName === asset.type);
        const controllerTypeValue = asset.controllerType || matchingAssetType?.controllerType || '';
        setControllerType(controllerTypeValue);
        setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);

        const assetParameters = buildAssetParametersForSelection(
            asset,
            matchingAssetType,
            validUoms
        );
        setParameters(assetParameters);
        setOriginalAssetData(buildOriginalAssetDataFromClick(asset, assetParameters));
    };

    useEffect(() => {
        const fetchPlantDetails = async () => {
            const plantGuid = localStorage.getItem('currentplantguid');
            const plantObjectId = localStorage.getItem('currentPlantId');

            if (!plantGuid && !plantObjectId) {
                setError('No plant ID found');
                navigate('/plants');
                return;
            }

            try {
                setLoading(true);
                setAssetHierarchy([]);
                setSelectedAssetFromHierarchy(null);
                setOriginalAssetData(null);

                const loaded = await loadCreatePlantDetailsData();
                setPlantData(loaded.plantData);
                setAssetHierarchy(loaded.assetHierarchy);
            } catch (err) {
                console.error('Failed to load plant details:', err);
                setError('Failed to load plant details');
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have an ID, it's different from previous, and we haven't already fetched it
        if (id && id !== prevPlantId && !isSaving && !isRefreshingAfterSave.current && id !== lastFetchedId.current) {
            // Additional check: only fetch if we don't already have matching plant data
            const currentPlantGuid = localStorage.getItem('currentplantguid');
            const currentPlantId = localStorage.getItem('currentPlantId');
            
            // Only fetch if the ID we're trying to fetch matches what's in localStorage
            // This prevents fetching when IDs are inconsistent
            if (id === currentPlantGuid || id === currentPlantId) {
                fetchPlantDetails();
                lastFetchedId.current = id;
            }
            setPrevPlantId(id);
        }
    }, [navigate, id, prevPlantId, isSaving]);

    // Cleanup effect when navigating away from plant details
    useEffect(() => {
        return () => {
            const currentPath = globalThis.location.pathname;
            // If navigating completely away from plant-related pages, clear state
            if (!currentPath.includes('create-plant') && !currentPath.includes('plants')) {
                localStorage.removeItem('currentPlantData');
                localStorage.removeItem('currentPlantId');
                localStorage.removeItem('currentplantguid');
                localStorage.removeItem('customerid');
            }
        };
    }, []);
    const updateControllerType = (selectedTypeName: string) => {
        const selectedAssetType = assetTypes.find(type => type.typeName === selectedTypeName);
        if (selectedAssetType) {
            const controllerTypeValue = selectedAssetType.controllerType || '';
            setControllerType(controllerTypeValue);
            // Only show the controllerType for the selected asset type if it exists
            setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);
        } else {
            setControllerType('');
            setAvailableControllerTypes([]);
        }
    };

    const hasAssetChanges = (): boolean =>
        detectAssetFormChanges(originalAssetData, selectedAssetFromHierarchy, {
            selectedAsset,
            assetName,
            assetStatus,
            gatewayDeviceType,
            gatewayDeviceId,
            simNumber,
            controllerType,
            controllerDeviceId,
            parameters,
        });

    const saveDisabled =
        Boolean(selectedAssetFromHierarchy) && !hasAssetChanges();
    const hasPlantAssets = plantHasSavedAssets(plantData);
    const stepOneClickable = Boolean(plantData || currentPlantData);

    const handleNext = () => {
        //Navigate to Notification page
        navigate('/create-plant-details/notification');


    }

    return {
        loading,
        plantData,
        currentPlantData,
        stepOneClickable,
        hasPlantAssets,
        handleTabNavigation,
        handleAddAsset,
        assetSearchText,
        handleAssetSearchChange,
        filteredAssets,
        selectedAssetFromHierarchy,
        handleAssetClick,
        handleDownloadConfigFiles,
        saveDisabled,
        handleSave,
        assetTypes,
        selectedAsset,
        handleTypeChange,
        assetStatus,
        setAssetStatus,
        assetName,
        setAssetName,
        gatewayDeviceType,
        setGatewayDeviceType,
        gatewayDeviceId,
        handleGatewaySerialChange,
        handleGatewaySerialBlur,
        gatewaySerialTouched,
        gatewaySerialError,
        simNumber,
        handleSimNumberChange,
        handleSimNumberBlur,
        simNumberTouched,
        simNumberError,
        controllerType,
        setControllerType,
        availableControllerTypes,
        controllerDeviceId,
        setControllerDeviceId,
        parameters,
        setParameters,
        validUoms,
        handleBack,
        handleCancel,
        handleNext,
    };
}
